import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { formatInr, rupeesToPaise } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Layers, Plus, Pencil, Ban, IndianRupee } from "lucide-react";

const EMPTY_PKG = {
    slug: "",
    name: "",
    tier_order: 1,
    monthly_price_paise: 0,
    description: "",
    is_active: true,
    features: {
        premium_listing: false,
        sponsored_search: false,
        max_active_posts: "",
        max_monthly_coupons_issued: "",
        video_ad_slots: 0,
        banner_ad_slots: 0,
        analytics_extended: false,
        priority_support: false,
    },
};

export default function PackagesPage() {
    const [rows, setRows] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = async () => {
        try {
            const res = await api.get("/admin/packages");
            setRows(res.data);
        } catch (e) {
            toast.error("Could not load packages");
        }
    };
    useEffect(() => {
        load();
    }, []);

    const openCreate = () => {
        setEditing({ ...EMPTY_PKG, features: { ...EMPTY_PKG.features } });
        setDialogOpen(true);
    };
    const openEdit = (pkg) => {
        setEditing({
            ...pkg,
            features: {
                ...pkg.features,
                max_active_posts: pkg.features?.max_active_posts ?? "",
                max_monthly_coupons_issued: pkg.features?.max_monthly_coupons_issued ?? "",
            },
        });
        setDialogOpen(true);
    };

    const toggleActive = async (pkg) => {
        try {
            if (pkg.is_active) {
                // Try DELETE (soft) — rejects if active subs.
                await api.delete(`/admin/packages/${pkg.id}`);
            } else {
                await api.patch(`/admin/packages/${pkg.id}`, { is_active: true });
            }
            await load();
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };

    return (
        <div className="mx-auto max-w-7xl px-6 py-8" data-testid="packages-page">
            <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-orange-600">
                        Product catalogue
                    </div>
                    <h1 className="text-3xl font-extrabold text-neutral-900">Packages</h1>
                    <p className="mt-1 text-sm text-neutral-500">Design the subscription tiers merchants will buy.</p>
                </div>
                <Button className="nest-gradient text-white" onClick={openCreate} data-testid="create-package-btn">
                    <Plus size={16} className="mr-2" /> Create package
                </Button>
            </header>

            <Card className="rounded-2xl border border-neutral-200">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[900px] text-sm" data-testid="packages-table">
                        <thead>
                            <tr className="border-b border-neutral-200 bg-neutral-50 text-left text-xs uppercase tracking-wide text-neutral-500">
                                <th className="px-4 py-3">Name</th>
                                <th className="px-4 py-3">Slug</th>
                                <th className="px-4 py-3 text-right">Tier</th>
                                <th className="px-4 py-3 text-right">Price / mo</th>
                                <th className="px-4 py-3">Features</th>
                                <th className="px-4 py-3">Active</th>
                                <th className="px-4 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows === null &&
                                Array.from({ length: 3 }).map((_, i) => (
                                    <tr key={i}>
                                        {Array.from({ length: 7 }).map((_, j) => (
                                            <td key={j} className="px-4 py-3">
                                                <Skeleton className="h-4 w-full" />
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            {rows?.map((pkg) => (
                                <tr key={pkg.id} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`pkg-row-${pkg.slug}`}>
                                    <td className="px-4 py-3 font-semibold text-neutral-800">{pkg.name}</td>
                                    <td className="px-4 py-3 font-mono text-xs text-neutral-500">{pkg.slug}</td>
                                    <td className="px-4 py-3 text-right">{pkg.tier_order}</td>
                                    <td className="px-4 py-3 text-right font-mono text-neutral-800">
                                        {formatInr(pkg.monthly_price_paise)}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex flex-wrap gap-1">
                                            {pkg.features?.premium_listing && (
                                                <Badge variant="outline" className="text-[10px]">Premium</Badge>
                                            )}
                                            {pkg.features?.sponsored_search && (
                                                <Badge variant="outline" className="text-[10px]">Sponsored</Badge>
                                            )}
                                            {pkg.features?.analytics_extended && (
                                                <Badge variant="outline" className="text-[10px]">Analytics+</Badge>
                                            )}
                                            {pkg.features?.priority_support && (
                                                <Badge variant="outline" className="text-[10px]">Priority</Badge>
                                            )}
                                            <Badge variant="outline" className="text-[10px] text-neutral-500">
                                                {pkg.features?.max_monthly_coupons_issued ?? "∞"} coupons/mo
                                            </Badge>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <Switch
                                            checked={pkg.is_active}
                                            onCheckedChange={() => toggleActive(pkg)}
                                            data-testid={`pkg-active-toggle-${pkg.slug}`}
                                        />
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => openEdit(pkg)}
                                            data-testid={`pkg-edit-${pkg.slug}`}
                                        >
                                            <Pencil size={14} className="mr-1" /> Edit
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                            {rows?.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="px-4 py-10 text-center text-sm text-neutral-500">
                                        No packages yet. Click "Create package" to add one.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>

            <PackageDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                pkg={editing}
                onSaved={() => {
                    setDialogOpen(false);
                    load();
                }}
            />
        </div>
    );
}

function PackageDialog({ open, onOpenChange, pkg, onSaved }) {
    const [busy, setBusy] = useState(false);
    const [local, setLocal] = useState(EMPTY_PKG);
    useEffect(() => {
        if (pkg) setLocal(pkg);
    }, [pkg]);

    const isEdit = !!pkg?.id;

    const submit = async () => {
        try {
            setBusy(true);
            const priceStr = String(local.monthly_price_paise);
            const paise = priceStr.includes(".") || priceStr.includes("₹") ? rupeesToPaise(priceStr) : Number(priceStr);
            const feats = { ...local.features };
            // Convert blanks to null (unlimited)
            feats.max_active_posts = feats.max_active_posts === "" || feats.max_active_posts === null ? null : Number(feats.max_active_posts);
            feats.max_monthly_coupons_issued = feats.max_monthly_coupons_issued === "" || feats.max_monthly_coupons_issued === null ? null : Number(feats.max_monthly_coupons_issued);
            feats.video_ad_slots = Number(feats.video_ad_slots || 0);
            feats.banner_ad_slots = Number(feats.banner_ad_slots || 0);
            const body = {
                slug: local.slug.toLowerCase().trim(),
                name: local.name.trim(),
                tier_order: Number(local.tier_order),
                monthly_price_paise: paise,
                description: local.description || null,
                is_active: !!local.is_active,
                features: feats,
            };
            if (isEdit) {
                await api.patch(`/admin/packages/${pkg.id}`, body);
                toast.success("Package updated");
            } else {
                await api.post("/admin/packages", body);
                toast.success("Package created");
            }
            onSaved?.();
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    const setField = (k, v) => setLocal((p) => ({ ...p, [k]: v }));
    const setFeat = (k, v) => setLocal((p) => ({ ...p, features: { ...p.features, [k]: v } }));

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl" data-testid="package-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Layers size={16} className="text-orange-600" />
                        {isEdit ? "Edit package" : "Create a new package"}
                    </DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label className="text-xs">Slug *</Label>
                        <Input
                            value={local.slug}
                            onChange={(e) => setField("slug", e.target.value)}
                            placeholder="platinum"
                            disabled={isEdit}
                            data-testid="pkg-slug"
                        />
                    </div>
                    <div>
                        <Label className="text-xs">Name *</Label>
                        <Input
                            value={local.name}
                            onChange={(e) => setField("name", e.target.value)}
                            placeholder="Platinum"
                            data-testid="pkg-name"
                        />
                    </div>
                    <div>
                        <Label className="text-xs">Tier order</Label>
                        <Input
                            type="number"
                            value={local.tier_order}
                            onChange={(e) => setField("tier_order", e.target.value)}
                            data-testid="pkg-tier"
                        />
                    </div>
                    <div>
                        <Label className="text-xs">Monthly price (₹) *</Label>
                        <div className="relative">
                            <IndianRupee size={14} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400" />
                            <Input
                                className="pl-7"
                                type="number"
                                min={0}
                                value={String(Number(local.monthly_price_paise || 0) / 100)}
                                onChange={(e) => setField("monthly_price_paise", Math.round(Number(e.target.value) * 100))}
                                data-testid="pkg-price"
                            />
                        </div>
                    </div>
                    <div className="col-span-2">
                        <Label className="text-xs">Description</Label>
                        <Textarea
                            rows={2}
                            value={local.description || ""}
                            onChange={(e) => setField("description", e.target.value)}
                            data-testid="pkg-desc"
                        />
                    </div>
                    <div className="col-span-2 grid grid-cols-2 gap-2 rounded-xl bg-orange-50 p-3">
                        <div className="col-span-2 text-xs font-bold uppercase tracking-wide text-orange-700">
                            Feature flags
                        </div>
                        {[
                            ["premium_listing", "Premium listing"],
                            ["sponsored_search", "Sponsored search"],
                            ["analytics_extended", "Extended analytics"],
                            ["priority_support", "Priority support"],
                        ].map(([k, label]) => (
                            <label key={k} className="flex items-center gap-2 text-sm text-neutral-700">
                                <Switch
                                    checked={!!local.features?.[k]}
                                    onCheckedChange={(v) => setFeat(k, v)}
                                    data-testid={`pkg-feat-${k}`}
                                />
                                {label}
                            </label>
                        ))}
                    </div>
                    <div className="col-span-2 grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs">Max active posts (blank = ∞)</Label>
                            <Input
                                type="number"
                                min={0}
                                value={local.features?.max_active_posts ?? ""}
                                onChange={(e) => setFeat("max_active_posts", e.target.value)}
                                data-testid="pkg-max-posts"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Max monthly coupons (blank = ∞)</Label>
                            <Input
                                type="number"
                                min={0}
                                value={local.features?.max_monthly_coupons_issued ?? ""}
                                onChange={(e) => setFeat("max_monthly_coupons_issued", e.target.value)}
                                data-testid="pkg-max-coupons"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Video ad slots</Label>
                            <Input
                                type="number"
                                min={0}
                                value={local.features?.video_ad_slots ?? 0}
                                onChange={(e) => setFeat("video_ad_slots", e.target.value)}
                                data-testid="pkg-video-slots"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Banner ad slots</Label>
                            <Input
                                type="number"
                                min={0}
                                value={local.features?.banner_ad_slots ?? 0}
                                onChange={(e) => setFeat("banner_ad_slots", e.target.value)}
                                data-testid="pkg-banner-slots"
                            />
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button disabled={busy} onClick={submit} className="nest-gradient text-white" data-testid="pkg-submit">
                        {busy ? "Saving…" : isEdit ? "Save changes" : "Create package"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
